import React, { useState, useEffect, useRef, useMemo } from 'react';
import DataTable from '../ui/DataTable';
import { StatusBadge, Chip } from '../ui/primitives';
import GroupedRowActionSheet, { ICON_PROPS } from '../ui/GroupedRowActionSheet';
import { Pencil, Settings, Copy, Trash2, Plus, Upload, Power, PowerOff } from 'lucide-react';
import PageActionsMenu, {
  ActionsMenuItem,
  ActionsSection,
} from '../ui/PageActionsMenu';
import { getCache, setCache, clearCacheByPrefix } from '../ui/listCache';
import DeleteConfirmDialog from '../ui/DeleteConfirmDialog';
import { Button } from '@mui/material'
import NoticeDialog from '../ui/NoticeDialog';
import FormDialog from './dialog';
import { MdEditNote, MdDelete, MdAddCircle } from "react-icons/md";
import { FaCopy } from "react-icons/fa";
import { FiSettings } from "react-icons/fi";
import PackageDialog from './packagedialog';
import AssessmentDialog from './assessmentdialog'
import axios from 'axios';
import CopyDialog from './copydialog';
import CourseSettingDialog from './CourseSettingDialog';
import { useConfirm } from '../ui/ConfirmProvider';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import DashboardNavbar from "../dashboardNavbar"
import ImportCourse from './ImportCourse';
import { LabelWithHelp } from '../ui/FieldHelp';
import { COURSE_LIST_FIELD_HELP as HELP } from '../ui/listScreenFieldHelp';
import {
  buildCourseListQuery,
  buildCourseListCacheKey,
  parseCourseListResponse,
  COURSE_LIST_DEFAULTS,
  COURSE_SORT_FIELDS,
  getNextCourseSort,
  sortCoursesLocally,
} from './courseListApi';
import { useAppContext } from '../../appprovider';
import { readResponseOrGate } from '../services/apiGate';
import {
  INSTRUCTOR_METERS,
  isMeterAtCap,
  meterGateBody,
} from '../services/instructorMeterLimits';
import CourseListStatsRow from './CourseListStatsRow';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const COURSE_PAGE_SIZE = 10;

const PageShell = styled.div`

  min-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};

  select {
    height: ${({ theme }) => theme.controlH};
    padding: 0 34px 0 12px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: ${({ theme }) => theme.rMd};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.text};
    font-size: ${({ theme }) => theme.fsSm};
    font-weight: 650;
    outline: none;
    box-shadow: 0 1px 2px rgba(17, 20, 38, 0.06);
  }

  @media (max-width: 768px) {
    padding: 0 14px 28px;
  }
`;

const PageHero = styled.div`
  width: min(1180px, 100%);
  margin: ${({ theme }) => theme.space} auto ${({ theme }) => theme.gap};
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.text};
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -.01em;
  }

  p {
    margin: 2px 0 0;
    color: ${({ theme }) => theme.textMuted};
    font-size: ${({ theme }) => theme.fsSm};
  }
`;

const TableWrap = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const initialValue = { courseId:"",courseName: "", code: "", vendor: "" , start: "", expire: "",enabled: "",domains:[],level:"", courseType:"", gradeLevel:"" }
const Course = () => {
  const askConfirmation = useConfirm();
  const { openUpgradeGate, entitlements, entitlementsLoading, loadEntitlements } = useAppContext();
  const { isLocked, guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [gridApi, setGridApi] = useState(null)
  const [tableData, setTableData] = useState(null)
  const [open, setOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [formData, setFormData] = useState(initialValue)
  const [loading, setLoading] = useState(false);
  const [isPackageDialogOpen, setPackageDialogOpen] = useState(false);
  const [isAssessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const userType = localStorage.getItem("userType");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogParams, setDialogParams] = useState({ courseId: '', userId: '' });
  const [userId, setUserId] = useState(localStorage.getItem('userId'));
   const navigate = useNavigate();
   const [columnDefs, setColumnDefs] = useState([]);
   const [isMobile,setIsMobile] = useState(false)
   const [importDialogBox,setImportDialogBox] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsCourse, setSettingsCourse] = useState(null);

   const menuItems = [];
  
    useEffect(() => {
      const planExpire = localStorage.getItem('planExpire');
  
      if (planExpire === "1" && userType!=2) {
        sessionStorage.removeItem('planRenewDismissed');
        navigate('/Portal/Instructor');
      }
    }, [navigate]);

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  /** Drop all cached course-list pages for this user after a mutation. */
  const invalidateCourseListCache = () => {
    if (userId) clearCacheByPrefix(`courses:${userId}:`);
  };

  const applyCourseListPage = (parsed) => {
    setTableData(parsed.content);
    setRowCount(parsed.totalElements);
    setCourseTotalPages(Math.max(parsed.totalPages, 1));
    setCoursePage(parsed.page);
  };

  const handleSelectionChanged = () => {
    const selectedNodes = gridApi.getSelectedNodes();
    const selectedData = selectedNodes.map(node => node.data);
    setSelectedRows(selectedData);
  };

  const [rowCount, setRowCount] = useState(0);
  const [coursePage, setCoursePage] = useState(0);
  const [courseTotalPages, setCourseTotalPages] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [courseSortBy, setCourseSortBy] = useState(COURSE_LIST_DEFAULTS.sortBy);
  const [courseSortDir, setCourseSortDir] = useState(COURSE_LIST_DEFAULTS.sortDir);
  const searchDebounceRef = useRef(null);
  const orgId = localStorage.getItem("orgId")

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedKeyword(searchKeyword);
      setCoursePage(0);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchKeyword]);

  useEffect(() => {
    if (userId) getUsers(coursePage, debouncedKeyword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, coursePage, debouncedKeyword]);

  const sortedTableData = useMemo(
    () => sortCoursesLocally(tableData || [], courseSortBy, courseSortDir),
    [tableData, courseSortBy, courseSortDir],
  );

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleAddCourse = () => {
    if (!guardAction('action.course.add')) return;
    if (isMeterAtCap(entitlements, INSTRUCTOR_METERS.courses)) {
      openUpgradeGate(meterGateBody(INSTRUCTOR_METERS.courses, entitlements));
      return;
    }
    setFormData(initialValue);
    handleClickOpen();
  };

  // UseEffect to update the userId state when it changes in localStorage
  useEffect(() => {
      const userIdFromStorage = localStorage.getItem('userId');
      setUserId(userIdFromStorage);
  }, []);

const rowSelection = 'multiple';

  const handleClose = () => {
    setOpen(false);
    setFormData(initialValue)
  };

  const formatDate = (dateString) => {
    if (!dateString) {
        return ''; // Return empty string if dateString is null or undefined
    }

    const date = new Date(dateString);
    
    // Check if the date is invalid
    if (isNaN(date.getTime())) {
        return ''; // Return an empty string if the date is invalid
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase(); // Convert month to 3-letter uppercase format
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
};

const getColumnDefs = (isMobile) => [
  {
    headerName: "Course Name",
    field: "courseName",
    checkboxSelection: !isMobile,
    valueFormatter: (params) => {
      const { courseName, price } = params.data || {};
      return price ? `$ ${courseName}` : courseName;
    }
  },
  { headerName: "Code", field: "code", hide: isMobile },
  { headerName: "Vendor", field: "vendor", hide: isMobile },
  {
    headerName: "Start", field: "start", hide: isMobile,
    valueFormatter: params => formatDate(params.value)
  },
  {
    headerName: "Expire", field: "expire", hide: isMobile,
    valueFormatter: params => formatDate(params.value)
  },
  {
    headerName: "Status", field: "active",
    cellRenderer: (params) => (params.value === 1 ? 'Enable' : 'Disable'),
  },
  {
    headerName: "Actions", field: "id", width: 400,
    cellRenderer: (params) => (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          overflowX: "auto",
          overflowY: "hidden",
          maxWidth: "100%",
          alignItems: "center",
        }}
      >
          <Button
                    variant="contained"
                    color="primary"
                    style={{
                        backgroundColor: 'purple',
                        color: 'white',
                        width: '8px',
                        height: '12px',
                        marginBottom: '4px',
                        fontSize: '8px',
                        padding: '4px 10px',
                    }}
                    onClick={() => handleEnabled(params.data)}
                >
                    {params.data.active === 1 ? 'Disable' : 'Enable'}
                </Button>
        <MdEditNote style={iconStyle("green")} title="Update" onClick={() => handleUpdate(params.data)} />
        <FiSettings style={iconStyle("blue")} title="Settings" onClick={(e) => { e.stopPropagation(); handleOpenSettings(params.data); }} />
        <MdDelete style={iconStyle("purple")} title="Delete" onClick={() => handleDelete(params.data.courseId, params.data.courseName)} />
        {userType === '2' && <FaCopy style={iconStyle("green")} title="Copy" onClick={() => handleCopy(params.data.courseId, params.data.courseName)} />}
      </div>
    )
  }
];

// Reusable icon style
const iconStyle = (color) => ({
  margin: "5px 10px",
  fontSize: "1.3em",
  color: color,
  cursor: "pointer"
});

useEffect(() => {
  const updateColumns = () => {
    const isMobile = window.innerWidth < 768;
    setColumnDefs(getColumnDefs(isMobile));
    setIsMobile(isMobile)
  };

  updateColumns(); // initial
  window.addEventListener("resize", updateColumns);
  return () => window.removeEventListener("resize", updateColumns);
}, []);

  const handleCopy = (courseId, courseName) => {
    if (!guardAction('action.course.copy')) return;
    setDialogOpen(true);
    setDialogParams({ courseId, courseName });
  };

  const handleOpenSettings = (row) => {
    setSettingsCourse(row);
    setSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
    setSettingsCourse(null);
  };
  

  const handleEnabled = async(data) =>{
    if (!guardAction('action.course.disable')) return;
    const action = data.active === 1 ? 'disable' : 'enable';
    const actionLabel = action.charAt(0).toUpperCase() + action.slice(1);
    const confirmed = await askConfirmation({
      title: `${actionLabel} course?`,
      message: `Are you sure you want to ${action} this course?`,
      confirmLabel: actionLabel,
    });
    if (!confirmed) return;

    try {
      const active = data.active === 1 ? 0 : 1;
      const body = {
        courseId: data.courseId,
        active,
      };
      await axios.post(apiUrl + '/api/course/updateEnabled', body, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });
      handleClose();
      invalidateCourseListCache();
      getUsers(coursePage, debouncedKeyword);
    } catch (err) {
      console.log(err.response ? err.response.data : 'Error');
    }
  }

  // Cache-first load: instant when returning from detail/back or pagination previous.
  const getUsers = (page = 0, keyword = '') => {
    const listParams = {
      page,
      size: COURSE_PAGE_SIZE,
      keyword,
    };
    const cacheKey = buildCourseListCacheKey(userId, listParams);
    const cached = getCache(cacheKey);
    if (cached) {
      applyCourseListPage(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    const query = buildCourseListQuery(listParams);
    fetch(`${apiUrl}/api/course/coursedomain/${userId}?${query}`, {
      headers: {
          'Content-Type': "application/json",
      "Accept": "*/*",
      "Access-Control-Allow-Origin": "*",
      'Authorization': getBasicAuthHeader(),
      },
    })
      .then(resp => resp.json())
      .then((resp) => {
        const parsed = parseCourseListResponse(resp);
        applyCourseListPage(parsed);
        setCache(cacheKey, parsed);
      })
      .catch(err => console.error('Error fetching courses:', err))
      .finally(() => setLoading(false));
  }

  const handleCoursePageChange = (nextPage) => {
    const zeroBasedPage = Math.max(nextPage - 1, 0);
    setCoursePage(zeroBasedPage);
  };

  const handleCourseSortChange = (columnKey) => {
    const next = getNextCourseSort(columnKey, courseSortBy, courseSortDir);
    setCourseSortBy(next.sortBy);
    setCourseSortDir(next.sortDir);
  };

  // Bulk delete selected courses. Endpoint + body documented for the API team:
  //   POST {API}/api/course/bulkDelete   body: { userId, courseIds: [<courseId>, ...] }
  // Typed delete confirmation shared by single + bulk delete.
  const [confirmState, setConfirmState] = useState({ open: false, count: 1, action: null });
  const askConfirm = (count, action) => setConfirmState({ open: true, count, action });
  const closeConfirm = () => setConfirmState({ open: false, count: 1, action: null });
  const runConfirm = async () => {
    const action = confirmState.action;
    closeConfirm();
    if (action) await action();
  };

  const handleBulkDelete = () => {
    if (!selectedRows.length) return;
    if (!guardAction('action.course.delete')) return;
    askConfirm(selectedRows.length, doBulkDelete);
  };

  const doBulkDelete = async () => {
    if (!selectedRows.length) return;
    const courseIds = selectedRows.map((r) => r.courseId);
    try {
      await axios.post(`${apiUrl}/api/course/bulkDelete`, { userId, courseIds }, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });
      setSelectedRows([]);
      invalidateCourseListCache();
      getUsers(coursePage, debouncedKeyword);
    } catch (error) {
      console.error('Bulk delete failed:', error);
      setAlertTitle('Unable to delete courses');
      setAlertMessage('Failed to delete the selected courses. Please try again.');
      setShowAlert(true);
    }
  };
  const onChange = (e) => {
    const { value, id } = e.target
    // console.log(value,id)
    setFormData({ ...formData, [id]: value })
  }

  // setting update row data to form data and opening pop up window
  const handleUpdate = (oldData) => {
    if (!guardAction('action.course.edit')) return;
    setFormData(oldData)
    handleClickOpen()
  }
  
  //deleting a course (typed confirmation)
  const handleDelete = (id, courseName) => {
    if (!id) { console.error("Invalid ID provided."); return; }
    if (!guardAction('action.course.delete')) return;
    askConfirm(1, () => doDelete(id, courseName));
  };

  const doDelete = async (id,courseName) => {
    if (!id) {
        console.error("Invalid ID provided.");
        return;
    }

    {
        try {
            // Check for assessments tied to the course
            const checkResponse = await axios.get(`${apiUrl}/api/Assessment/findCourse/${id}`, {
              headers: {
                  'Content-Type': "application/json",
                  "Accept": "*/*",
                  "Access-Control-Allow-Origin": "*",
                  'Authorization': getBasicAuthHeader(),
              },
          });
          
          if (checkResponse.data && checkResponse.data.length > 0) {
              // Extract the names of the assessments from the response data
              const assessmentNames = checkResponse.data.map((assessment) => assessment.name);
              const numberOfAssessments = assessmentNames.length;
              const assessmentsList = assessmentNames.join("\n");
          
              const assessmentWord = numberOfAssessments === 1 ? 'assessment' : 'assessments';
              const hasWord = numberOfAssessments === 1 ? 'has' : 'have';
              setAlertTitle(`Unable to delete course: ${courseName}`);
              setAlertMessage(
                `This course ${hasWord} ${numberOfAssessments} associated ${assessmentWord}:\n${assessmentsList}\n\nPlease delete the associated assessments first.`,
              );
              setShowAlert(true);
              return;
          }
          

            // If no assessments, proceed with delete
            const deleteResponse = await axios.delete(`${apiUrl}/api/course/${userId}&${id}`, {
                headers: {
                  'Content-Type': "application/json",
                  "Accept": "*/*",
                  "Access-Control-Allow-Origin": "*",
                  'Authorization': getBasicAuthHeader(),
                },
            });

            if (deleteResponse.status === 200) {
                console.log("Course deleted successfully.");
                invalidateCourseListCache();
                getUsers(coursePage, debouncedKeyword);
            } else {
                console.error("Error deleting course:", deleteResponse.statusText);
            }
        } catch (error) {
            console.error("Network error:", error);
        }
    }
};
  const handleFormSubmit = async () => {
    const featureKey = formData.courseId ? 'action.course.edit' : 'action.course.add';
    if (!guardAction(featureKey)) return;
    setLoading(true); // Set loading to true when submitting form
    if (formData.courseId) {
        // Updating a course. Save is already an explicit action inside the edit
        // dialog, so we don't gate it behind a second typed-confirm dialog — that
        // dialog renders outside the MUI Dialog's focus trap, so its input can't be
        // focused/typed, which left the update silently stuck (loader spinning).
        fetch(apiUrl+`/api/course/${userId}&${formData.courseId}`, {
            method: "PUT",
            body: JSON.stringify(formData),
              headers: {
                  'Content-Type': "application/json",
              "Accept": "*/*",
              "Access-Control-Allow-Origin": "*",
              'Authorization': getBasicAuthHeader(),
              },
        })
        .then(resp => resp.json())
        .then(resp => {
            handleClose();
            window.location.reload();
            getUsers(coursePage, debouncedKeyword);
        })
        .catch(error => {
            console.error("Error updating course:", error);
        })
        .finally(() => {
            setLoading(false); // Set loading to false after form submission is complete
        });
    } else {
        // Adding new course
        //console.log(formData)
        fetch(apiUrl+`/api/course/coursedomain/${userId}&${orgId}`, {
            method: "POST",
            body: JSON.stringify(formData),
              headers: {
                  'Content-Type': "application/json",
              "Accept": "*/*",
              "Access-Control-Allow-Origin": "*",
              'Authorization': getBasicAuthHeader(),
              },
        })
        .then(async (resp) => {
          const gateResult = await readResponseOrGate(resp);
          if (gateResult.gate) {
            openUpgradeGate(gateResult.body);
            return null;
          }
          return gateResult.response.json();
        })
        .then(resp => {
          if (!resp) return;
          handleClose();
          if (userId) loadEntitlements(userId);
          window.location.reload();
          setTimeout(() => {
              getUsers(coursePage, debouncedKeyword);
          }, 1000);
      })
        .catch(error => {
            console.error("Error adding new course:", error);
        })
        .finally(() => {
            setLoading(false); // Set loading to false after form submission is complete
        });
    }
    
};
  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  return (
    <PageShell>
      <DashboardNavbar menuItems={menuItems} />
      <PageHero>
        <div>
          <h2>Course catalog</h2>
          <p>Manage course setup, imports, files, and settings.</p>
        </div>
        <PageActionsMenu
          primaryLabel="Add course"
          primaryIcon={<Plus size={16} strokeWidth={2.25} aria-hidden="true" />}
          onPrimaryClick={handleAddCourse}
          primaryLocked={isLocked('action.course.add')}
          menuTitle="Course catalog tools"
          menuSubtitle="Create courses or import from a file."
        >
          {(closeActions) => (
            <ActionsSection label="Import">
              <ActionsMenuItem
                label="Import course"
                description="Upload course from PDF or DOCX"
                icon={<Upload size={15} strokeWidth={1.9} />}
                closeActions={closeActions}
                onClick={() => setImportDialogBox(true)}
                {...lockProps('action.course.import')}
              />
            </ActionsSection>
          )}
        </PageActionsMenu>
      </PageHero>
      <NoticeDialog
        open={showAlert}
        title={alertTitle}
        message={alertMessage}
        onClose={handleCloseAlert}
      />
      <TableWrap>
        <CourseListStatsRow
          entitlements={entitlements}
          entitlementsLoading={entitlementsLoading}
          rowCount={rowCount}
          rows={sortedTableData}
          loading={loading}
          totalPages={courseTotalPages}
        />
        <DataTable
          rows={sortedTableData}
          loading={loading}
          rowKey={(r, i) => r.courseId ?? r.id ?? i}
          selectable
          pageSize={COURSE_PAGE_SIZE}
          serverSide
          page={coursePage + 1}
          totalPages={courseTotalPages}
          totalElements={rowCount}
          onPageChange={handleCoursePageChange}
          sortBy={courseSortBy}
          sortDir={courseSortDir}
          onSortChange={handleCourseSortChange}
          searchValue={searchKeyword}
          onSearchChange={setSearchKeyword}
          onSelectionChange={setSelectedRows}
          bulkActions={(
            <Chip
              $soft
              style={{
                cursor: isLocked('action.course.delete') ? 'not-allowed' : 'pointer',
                color: '#d6455d',
                opacity: isLocked('action.course.delete') ? 0.55 : 1,
              }}
              onClick={handleBulkDelete}
            >
              Delete selected
            </Chip>
          )}
          onRowClick={(r) => navigate('/Portal/Instructor/CourseDetail', { state: { course: r } })}
          search
          searchPlaceholder="Search courses by title or code…"
          filters={[
            { key: 'all', label: 'All', test: () => true },
            { key: 'enabled', label: 'Enabled', test: (r) => r.active === 1 },
            { key: 'disabled', label: 'Disabled', test: (r) => r.active !== 1 },
          ]}
          primary={{
            header: 'Course',
            sortKey: COURSE_SORT_FIELDS.courseName,
            label: (r) => (r.price ? `$ ${r.courseName}` : r.courseName),
            sub: (r) => r.vendor,
            short: (r) => (r.code ? String(r.code).replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase() : undefined),
          }}
          columns={[
            { header: 'Code', sortKey: COURSE_SORT_FIELDS.code, render: (r) => r.code, muted: true },
            { header: 'Start', sortKey: COURSE_SORT_FIELDS.start, render: (r) => formatDate(r.start), muted: true, mono: true },
            { header: 'Expire', sortKey: COURSE_SORT_FIELDS.expire, render: (r) => formatDate(r.expire), muted: true, mono: true },
            {
              header: <LabelWithHelp help={HELP.status}>Status</LabelWithHelp>,
              sortKey: COURSE_SORT_FIELDS.active,
              render: (r) => (
                <StatusBadge
                  $active={r.active === 1}
                  style={{ cursor: 'pointer' }}
                  title={r.active === 1 ? 'Disable' : 'Enable'}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEnabled(r);
                  }}
                >
                  {r.active === 1 ? 'Enabled' : 'Disabled'}
                </StatusBadge>
              ),
            },
          ]}
          actionsColumnWidth={52}
          actions={(r) => (
            <GroupedRowActionSheet
              entityLabel="Course"
              title={r.courseName}
              menuAriaLabel="Course actions"
              groupLabel="For this course"
              groupItems={[
                { key: 'settings', icon: <Settings {...ICON_PROPS} />, label: 'Course settings' },
                ...(userType === '2'
                  ? [{
                    key: 'copy',
                    icon: <Copy {...ICON_PROPS} />,
                    label: 'Copy course',
                    ...lockProps('action.course.copy'),
                  }]
                  : []),
              ]}
              items={[
                {
                  key: 'edit',
                  icon: <Pencil {...ICON_PROPS} />,
                  label: 'Edit',
                  ...lockProps('action.course.edit'),
                },
                {
                  key: 'toggle-active',
                  icon: r.active === 1
                    ? <PowerOff {...ICON_PROPS} />
                    : <Power {...ICON_PROPS} />,
                  label: r.active === 1 ? 'Disable course' : 'Enable course',
                  ...lockProps('action.course.disable'),
                },
              ]}
              dangerItem={{
                key: 'delete',
                icon: <Trash2 {...ICON_PROPS} />,
                label: 'Delete',
                ...lockProps('action.course.delete'),
              }}
              onAction={(key) => {
                if (key === 'settings') handleOpenSettings(r);
                else if (key === 'copy') handleCopy(r.courseId, r.courseName);
                else if (key === 'edit') handleUpdate(r);
                else if (key === 'toggle-active') handleEnabled(r);
                else if (key === 'delete') handleDelete(r.courseId, r.courseName);
              }}
            />
          )}
          emptyText="No courses yet."
        />
      </TableWrap>
      <CopyDialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        courseId={dialogParams.courseId}
        courseName = {dialogParams.courseName}
      />
      <AssessmentDialog open={isAssessmentDialogOpen} onChange={onChange} handleClose={()=> setAssessmentDialogOpen(false)}/>
      <PackageDialog open={isPackageDialogOpen} handleClose={()=> setPackageDialogOpen(false)}/>
      <FormDialog open={open} handleClose={handleClose}
        data={formData} onChange={onChange} handleFormSubmit={handleFormSubmit} loading={loading}/>
        <ImportCourse
        open={importDialogBox}
        onClose={() => setImportDialogBox(false)}
      />
      <DeleteConfirmDialog
        open={confirmState.open}
        count={confirmState.count}
        onClose={closeConfirm}
        onConfirm={runConfirm}
      />
      <CourseSettingDialog
        open={settingsOpen}
        courseDetails={settingsCourse}
        onClose={handleCloseSettings}
        saveLocked={isLocked('action.course.settings')}
        onSaveBlocked={() => setLockedDialogOpen(true)}
        onSaved={() => {
          invalidateCourseListCache();
          getUsers(coursePage, debouncedKeyword);
        }}
      />
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </PageShell>
  );
}

export default Course;
